// 005 FR-025 (P-PROD-6): the server's cart at a glance. Wide web: a right-rail panel with each child's
// subtotal and items, the subtotal and View cart. Phones and narrow web: a bottom bar with the item
// count, total and View cart. The selected child's group is highlighted (FR-002).
import { Link } from "expo-router";
import { StyleSheet, View } from "react-native";

import { makeStyles } from "@/theme/makeStyles";
import { Button } from "@/ui/Button";
import { Text } from "@/ui/Text";

import { SHOP_COPY } from "./copy";
import { useCart } from "./useCart";
import { formatPaise } from "./format";
import { useShopping } from "./shopApi";

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    bar: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: t.space.md,
      paddingHorizontal: t.space.lg,
      paddingVertical: t.space.sm,
      backgroundColor: t.color.accent,
    },
    onAccent: { color: t.color.onAccent },
    barButton: { minWidth: 140 },
    panel: {
      width: 300,
      padding: t.space.lg,
      gap: t.space.sm,
      borderRadius: t.radius.card,
      backgroundColor: t.color.surface,
      alignSelf: "flex-start",
    },
    group: { gap: t.space.xxs, paddingVertical: t.space.xs },
    groupOn: {
      borderLeftWidth: 3,
      borderLeftColor: t.color.primary,
      paddingLeft: t.space.sm,
    },
    row: {
      flexDirection: "row",
      justifyContent: "space-between",
      gap: t.space.sm,
    },
  }),
);

export function CartSummary({
  variant,
  selectedStudentId,
}: {
  variant: "bar" | "panel";
  selectedStudentId: string | null;
}) {
  const styles = useStyles();
  const { cart } = useCart();
  const { cartHref, onViewCart } = useShopping();
  if (!cart) return null;
  const view = onViewCart ? (
    <Button onPress={onViewCart} testID="view-cart">
      {SHOP_COPY.viewCart}
    </Button>
  ) : (
    <Link href={cartHref} asChild>
      <Button accessibilityRole="link" testID="view-cart">
        {SHOP_COPY.viewCart}
      </Button>
    </Link>
  );
  if (variant === "bar") {
    if (cart.itemCount === 0) return null;
    return (
      <View
        style={styles.bar}
        testID="cart-bar"
        role="region"
        aria-label={SHOP_COPY.cartPanel}
      >
        <Text weight="semiBold" style={styles.onAccent}>
          {SHOP_COPY.cartBar(
            cart.itemCount,
            formatPaise(cart.itemsTotalInPaise),
          )}
        </Text>
        <View style={styles.barButton}>{view}</View>
      </View>
    );
  }
  return (
    <View
      style={styles.panel}
      testID="cart-panel"
      role="region"
      aria-label={SHOP_COPY.cartPanel}
    >
      <Text weight="bold" size="lg">
        {SHOP_COPY.cartPanel}
      </Text>
      {cart.itemCount === 0 ? (
        <Text tone="muted">{SHOP_COPY.cartEmpty}</Text>
      ) : (
        <>
          {cart.children.map((c) => {
            const items = c.lines.reduce((n, l) => n + l.quantity, 0);
            return (
              <View
                key={c.studentId}
                style={[
                  styles.group,
                  c.studentId === selectedStudentId && styles.groupOn,
                ]}
              >
                <View style={styles.row}>
                  <Text weight="semiBold">{`${c.name.split(" ")[0]} — ${items} ${items === 1 ? "item" : "items"}`}</Text>
                  <Text weight="semiBold">
                    {formatPaise(c.subtotalInPaise)}
                  </Text>
                </View>
                <Text size="sm" tone="muted" numberOfLines={2}>
                  {c.lines.map((l) => l.name).join(", ")}
                </Text>
              </View>
            );
          })}
          <View style={styles.row}>
            <Text weight="bold">{SHOP_COPY.subtotal}</Text>
            <Text weight="bold">{formatPaise(cart.itemsTotalInPaise)}</Text>
          </View>
          {view}
        </>
      )}
    </View>
  );
}
